import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { CERTIFICATIONS, LANGUAGES, EDUCATION } from "@/data/content";

export default function Philosophy() {
  return (
    <section
      id="credentials"
      className="relative py-24 md:py-32 bg-[#0a0a0a] overflow-hidden"
      data-testid="credentials-section"
    >
      {/* Marquee background */}
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 pointer-events-none select-none opacity-90">
        <Marquee speed={40} gradient={false}>
          <span className="font-heading uppercase text-stroke text-[16vw] leading-none whitespace-nowrap pr-10">
            AFC B · AFC C · AIFF D · CERTIFIED ·&nbsp;
          </span>
        </Marquee>
      </div>

      <div className="relative container mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-14">
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">04 / Credentials</span>
          <span className="h-px flex-1 bg-neutral-800" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Philosophy card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-[#050505] border border-neutral-800 p-8 md:p-12"
            data-testid="philosophy-card"
          >
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-volt mb-6">Coaching Philosophy</p>
            <blockquote className="font-heading uppercase text-3xl md:text-4xl leading-tight text-white">
              &ldquo;Talent opens the door.
              <span className="text-volt"> Discipline, structure and belief</span> keep it open.&rdquo;
            </blockquote>
            <p className="mt-6 font-body font-light text-neutral-300 leading-relaxed">
              Every session is built around maximising player potential — sharp talent evaluation, individual
              development plans, tactical clarity and a positive, high-performance environment where players and
              staff grow together.
            </p>

            {/* Languages */}
            <div className="mt-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">Languages</p>
              <div className="flex flex-wrap gap-2">
                {LANGUAGES.map((l) => (
                  <span
                    key={l}
                    className="font-mono text-xs uppercase tracking-wide border border-neutral-800 px-3 py-2 text-neutral-300"
                  >
                    {l}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Certifications + Education */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-volt text-black p-8"
              data-testid="certifications-card"
            >
              <p className="font-mono text-xs uppercase tracking-[0.2em] mb-6">Licences</p>
              <div className="space-y-4">
                {CERTIFICATIONS.map((c) => (
                  <div key={c.code} className="flex items-baseline justify-between border-b border-black/20 pb-3">
                    <span className="font-heading uppercase text-2xl">{c.label}</span>
                    <span className="font-mono text-sm">{c.year}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#050505] border border-neutral-800 p-8"
              data-testid="education-card"
            >
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-volt mb-6">Education</p>
              <div className="space-y-4">
                {EDUCATION.map((e) => (
                  <div key={e.degree}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-heading uppercase text-lg text-white">{e.degree}</span>
                      <span className="font-mono text-xs text-neutral-500">{e.year}</span>
                    </div>
                    <p className="font-body text-sm text-neutral-400">{e.school}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
